/**
 * AdminDashboard — at-a-glance operational view for SuperAdmins.
 *
 * Renders when a logged-in admin lands on /dashboard. Groups the most
 * important operational metrics into a scannable dashboard: MRR + cash
 * receipts + churn from the existing /invoices/admin/mrr endpoint, KYC
 * backlog + platform stats from /admin/stats, outstanding invoices, top
 * accounts, quick-action shortcuts.
 *
 * Each card links to the deeper page where the admin can act on the number.
 * All requests are best-effort — a single failing endpoint renders a "—"
 * cell without breaking the rest of the dashboard.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
    Building2, CreditCard, TrendingUp, AlertTriangle, Users, FileText,
    Wallet, Activity, PieChart, Sparkles, Headphones, ChevronRight, Shield,
} from "lucide-react";

function fmt(n) {
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Math.round(n || 0));
}

function KpiCard({ icon: Icon, label, value, sub, tone, to, testid }) {
    const toneCls = {
        ikb: "border-l-ikb text-ikb",
        emerald: "border-l-emerald-500 text-emerald-700",
        amber: "border-l-amber-500 text-amber-700",
        rose: "border-l-rose-500 text-rose-700",
        blue: "border-l-blue-500 text-blue-700",
    }[tone] || "border-l-ikb text-ikb";
    const Content = (
        <div className={`border-l-4 border-t border-r border-b border-border bg-white p-4 transition-all ${to ? "hover:border-ikb cursor-pointer" : ""} ${toneCls}`} data-testid={testid}>
            <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest">
                <Icon className="w-3 h-3" />
                <span>{label}</span>
            </div>
            <div className="font-display font-black text-2xl mt-1 tabular-nums text-foreground">{value}</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>
        </div>
    );
    return to ? <Link to={to}>{Content}</Link> : Content;
}

export default function AdminDashboard() {
    const [mrr, setMrr] = useState(null);
    const [stats, setStats] = useState(null);
    const [pendingKyc, setPendingKyc] = useState([]);
    const [recentImp, setRecentImp] = useState([]);
    const [signupDefault, setSignupDefault] = useState("free");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            const [m, s, kyc, sd] = await Promise.all([
                api.get("/invoices/admin/mrr").then((r) => r.data).catch(() => null),
                api.get("/admin/stats").then((r) => r.data).catch(() => null),
                api.get("/admin/companies?status=pending").then((r) => r.data).catch(() => []),
                api.get("/admin/signup-defaults").then((r) => r.data).catch(() => ({ default_plan: "free" })),
            ]);
            setMrr(m);
            setStats(s);
            setPendingKyc(Array.isArray(kyc) ? kyc : []);
            setSignupDefault(sd?.default_plan || "free");
            setLoading(false);
        })();
    }, []);

    return (
        <div className="max-w-[1600px] mx-auto px-3 sm:px-4 py-3 sm:py-5 space-y-6" data-testid="admin-dashboard">
            {/* Header */}
            <header className="pb-3 border-b border-border">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Super admin</div>
                <h1 className="font-display font-black text-2xl sm:text-3xl">Platform overview</h1>
                <p className="text-xs text-muted-foreground mt-1">
                    At-a-glance revenue, employer health and pending admin work. Click any card to jump to the deeper view.
                </p>
            </header>

            {/* Revenue row */}
            <section>
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Revenue</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <KpiCard testid="ad-mrr-sub" icon={TrendingUp} label="Subscription MRR"
                        value={mrr ? `₹${fmt(mrr.subscription_mrr_inr)}` : "—"}
                        sub="On the books · monthly" tone="ikb" to="/admin/subscriptions" />
                    <KpiCard testid="ad-mrr-cash" icon={Wallet} label="Cash MRR (30d)"
                        value={mrr ? `₹${fmt(mrr.cash_mrr_inr)}` : "—"}
                        sub="Actual receipts · trailing 30d" tone="emerald" to="/admin/subscriptions" />
                    <KpiCard testid="ad-paying" icon={Activity} label="Paying accounts"
                        value={mrr ? fmt(mrr.paying_companies) : "—"}
                        sub={mrr?.churn_pct != null ? `Churn ${mrr.churn_pct}%` : ""} tone="amber" to="/admin/employers" />
                    <KpiCard testid="ad-churn" icon={PieChart} label="Churned (30d)"
                        value={mrr ? fmt(mrr.churned_last_30d) : "—"}
                        sub="Cancelled + downgraded" tone="rose" to="/admin/subscriptions" />
                </div>
            </section>

            {/* Platform stats row */}
            <section>
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Platform</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    <KpiCard testid="ad-companies" icon={Building2} label="Employers"
                        value={stats ? fmt(stats.companies) : "—"} sub="Total accounts" tone="blue" to="/admin/employers" />
                    <KpiCard testid="ad-kyc" icon={AlertTriangle} label="Pending KYC"
                        value={stats ? fmt(stats.pending_kyc) : "—"} sub="Awaiting approval" tone="amber" to="/admin/employers?status=pending" />
                    <KpiCard testid="ad-users" icon={Users} label="Users"
                        value={stats ? fmt(stats.users) : "—"} sub="All tenants" tone="ikb" />
                    <KpiCard testid="ad-jobs" icon={FileText} label="Jobs"
                        value={stats ? fmt(stats.jobs) : "—"} sub="All tenants" tone="blue" />
                    <KpiCard testid="ad-candidates" icon={Users} label="Candidates"
                        value={stats ? fmt(stats.candidates) : "—"} sub="All tenants" tone="emerald" />
                </div>
            </section>

            {/* Two-column: top accounts + admin actions */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Top accounts */}
                <section className="border border-border bg-white" data-testid="ad-top-accounts">
                    <div className="px-4 py-2 border-b border-border flex items-center justify-between">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Top accounts by MRR</div>
                        <Link to="/admin/subscriptions" className="text-[10px] font-mono uppercase tracking-widest text-ikb hover:underline">See all →</Link>
                    </div>
                    {loading ? (
                        <div className="p-4 text-xs text-muted-foreground">Loading…</div>
                    ) : (mrr?.top_accounts || []).length === 0 ? (
                        <div className="p-4 text-xs text-muted-foreground">No paid accounts yet.</div>
                    ) : (
                        <table className="w-full text-xs">
                            <tbody>
                                {mrr.top_accounts.slice(0, 6).map((a, i) => (
                                    <tr key={a.company_id} className="border-b border-border last:border-b-0">
                                        <td className="px-3 py-2 tabular-nums text-muted-foreground w-8">{i + 1}</td>
                                        <td className="px-2 py-2 font-medium truncate max-w-[220px]">{a.company_name}</td>
                                        <td className="px-2 py-2 uppercase tracking-widest font-mono text-[10px] text-muted-foreground">{a.plan_key}</td>
                                        <td className="px-3 py-2 tabular-nums text-right font-bold">₹{fmt(a.mrr)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </section>

                {/* Pending KYC list */}
                <section className="border border-border bg-white" data-testid="ad-pending-kyc">
                    <div className="px-4 py-2 border-b border-border flex items-center justify-between">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Pending KYC · {pendingKyc.length}</div>
                        <Link to="/admin/employers?status=pending" className="text-[10px] font-mono uppercase tracking-widest text-ikb hover:underline">Review all →</Link>
                    </div>
                    {loading ? (
                        <div className="p-4 text-xs text-muted-foreground">Loading…</div>
                    ) : pendingKyc.length === 0 ? (
                        <div className="p-4 text-xs text-muted-foreground">All caught up — no pending KYC.</div>
                    ) : (
                        <ul className="divide-y divide-border">
                            {pendingKyc.slice(0, 6).map((c) => (
                                <li key={c.id} className="px-3 py-2 flex items-center gap-2 text-xs hover:bg-neutral-50">
                                    <Building2 className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                                    <span className="font-medium truncate flex-1">{c.name}</span>
                                    <span className="text-[10px] font-mono text-muted-foreground">{c.created_at?.slice(0, 10)}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            </div>

            {/* Signup defaults + quick actions */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <section className="border border-border bg-white p-4" data-testid="ad-signup-defaults">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Signup defaults</div>
                    <div className="text-sm">
                        New employer sign-ups are auto-assigned to the <b className="text-ikb uppercase font-mono">{signupDefault}</b> plan.
                    </div>
                    <Link to="/admin/subscriptions#signup-defaults" className="mt-3 inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest text-ikb hover:underline">
                        Change default plan <ChevronRight className="w-3 h-3" />
                    </Link>
                </section>

                <section className="border border-border bg-white p-4" data-testid="ad-quick-actions">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Quick actions</div>
                    <div className="flex flex-wrap gap-2">
                        <Link to="/admin/employers" className="inline-flex items-center gap-1.5 h-8 px-3 border border-border bg-white hover:border-ikb text-xs">
                            <Building2 className="w-3.5 h-3.5" /> Manage employers
                        </Link>
                        <Link to="/admin/features" className="inline-flex items-center gap-1.5 h-8 px-3 border border-border bg-white hover:border-ikb text-xs">
                            <Sparkles className="w-3.5 h-3.5" /> Feature toggles
                        </Link>
                        <Link to="/admin/subscriptions" className="inline-flex items-center gap-1.5 h-8 px-3 border border-border bg-white hover:border-ikb text-xs">
                            <CreditCard className="w-3.5 h-3.5" /> Subscriptions
                        </Link>
                        <Link to="/admin/helpdesk" className="inline-flex items-center gap-1.5 h-8 px-3 border border-border bg-white hover:border-ikb text-xs">
                            <Headphones className="w-3.5 h-3.5" /> Helpdesk
                        </Link>
                        <Link to="/admin" className="inline-flex items-center gap-1.5 h-8 px-3 border border-border bg-white hover:border-ikb text-xs">
                            <Shield className="w-3.5 h-3.5" /> Full console
                        </Link>
                    </div>
                </section>
            </div>
        </div>
    );
}
